import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import {
  PROJECT_POSITION_STATUSES_READONLY,
  erfuelltRecht,
  positionStatusReasonRequired,
  type ProjectPositionStatus,
} from '@symphenia/shared-types'
import { Blaettern, type PositionsNavigation } from '@/components/positionswahl/PositionsWahl'
import { apiErrorText } from '@/lib/api-error'
import { formCard } from '@/lib/form-styles'
import { useAuthStore } from '@/lib/auth.store'
import { FeldStatus, FeldStatusZeichen, useFeld } from '@/lib/speichern'
import { useTerminology } from '@/lib/useTerminology'
import {
  approveSupplement,
  changePositionStatus,
  deletePosition,
  releasePosition,
  type PositionDeleted,
} from '../api/projects-phase-d.api'
import {
  POSITION_STATUS_LABELS,
  alsZeitpunkt,
  kennzeile,
  kennzeileTeile,
  positionStatusText,
  APPROVAL_KIND_LABELS,
  ausloeserText,
} from '../project-labels'
import type { PositionDetail, PositionRow } from '../types/phase-d.types'
import type { ProjectDetail } from '../types/project.types'
import { PositionWorkStepsTab } from './PositionWorkStepsTab'
import { PositionTechTab } from './PositionTechTab'
import { PositionBomTab } from './PositionBomTab'
import { PositionSalesTab } from './PositionSalesTab'
import { PositionDetailsTab, usePositionStelle } from './PositionDetailsTab'
import { PositionTermineTab } from './PositionTermineTab'
import { Kennzeile } from './Kennzeile'
import { PositionConsumablesTab } from './PositionConsumablesTab'
import { ActualEntriesList } from './ActualEntriesList'
import { ProjectControllingTab } from './ProjectControllingTab'
import { ProjectFilesPanel } from './ProjectFilesPanel'
import { PositionNavigator } from './PositionNavigator'
import { bestaetige, frage } from '@/lib/dialoge'

// Reihenfolge des Auftraggebers (19.9.2026): vom Plan über die Ausführung zum Geld.
const REITER = [
  { key: 'details', label: 'Details' },
  { key: 'termine', label: 'Phasen & Zuständige' },
  { key: 'tech', label: 'Technik' },
  { key: 'bom', label: 'Stückliste' },
  { key: 'consumables', label: 'Verbrauchsmaterial' },
  { key: 'steps', label: 'Arbeitsschritte' },
  { key: 'actuals', label: 'Arbeitszeiten' },
  { key: 'files', label: 'Dokumente' },
  { key: 'sales', label: 'Verkauf' },
  { key: 'controlling', label: 'Nachkalkulation' },
  { key: 'history', label: 'Verlauf' },
] as const

type ReiterKey = (typeof REITER)[number]['key']

/**
 * **Die Überschrift der Position** — die Kennzeile „1 PA Treppe mit Podest" in einem Rahmen, der rechts mit dem
 * Pfeil die Positionsliste aufklappt (Nachtrag 15).
 *
 * Nachtrag 16 (Auftraggeber 13.10.2026): Die **Bezeichnung lässt sich hier ändern** — sie ist ein Eingabefeld im
 * Rahmen, Menge und Einheit stehen als Text davor (geändert werden sie im Reiter Details). Gespeichert wird beim
 * Verlassen des Felds über die eine Speicherstelle der Position, wie im Reiter Details. Die Liste öffnet dann der
 * Pfeil, nicht mehr der Text — wie an der Überschrift der Kalkulation. Der Rahmen ist nach rechts breiter: Zwischen
 * Text und Pfeil bleibt Luft, und eine kurze Bezeichnung macht ihn nicht schmal.
 *
 * Gesperrt (ohne Recht, storniert) bleibt die Überschrift der Knopf, der die Liste aufklappt.
 */
function KopfBezeichnung({
  projekt,
  position,
  gesperrt,
  wahl,
  onChanged,
}: {
  projekt: ProjectDetail
  position: PositionDetail
  gesperrt: boolean
  wahl: PositionsNavigation
  onChanged: () => Promise<void> | void
}) {
  const terms = useTerminology()
  const stelle = usePositionStelle(projekt, position, onChanged)
  const name = useFeld(stelle, 'name', { label: 'Bezeichnung', pflicht: true, gesperrt })
  const titel = `${terms.positionPlural} zeigen (Alt + ↓)`
  const schrift = 'text-[22px] leading-7'

  if (gesperrt) {
    return (
      <div className="relative flex min-w-0">
        <button
          type="button"
          className="inline-flex min-w-[14rem] max-w-full items-center justify-between gap-8 rounded-md border border-border/70 bg-background py-0.5 pl-3 pr-2.5 text-left hover:border-primary aria-expanded:border-primary"
          aria-expanded={wahl.offen}
          title={titel}
          onClick={() => wahl.setOffen(!wahl.offen)}
        >
          <h2 className={`min-w-0 truncate ${schrift} font-semibold`} title={kennzeile(position)}>
            <Kennzeile position={position} />
          </h2>
          <ChevronDown className="h-[18px] w-[18px] shrink-0 text-muted-foreground" />
        </button>
        {wahl.offen && wahl.fenster}
      </div>
    )
  }

  const { menge } = kennzeileTeile(position)
  return (
    <div className="relative min-w-0">
      <div
        className={`flex min-w-0 items-stretch rounded-md border bg-background focus-within:border-ring ${
          name.fehler ? 'border-destructive' : wahl.offen ? 'border-primary' : 'border-border/70 hover:border-primary'
        }`}
      >
        <h2 className={`flex min-w-0 items-center py-0.5 pl-3 ${schrift}`}>
          {menge && <span className="shrink-0 whitespace-pre">{menge} </span>}
          {/* Das Feld ist so breit wie sein Text: Der unsichtbare Zwilling gibt die Breite vor, das Feld liegt darüber. */}
          <span className="relative block min-w-[5rem] shrink overflow-hidden">
            <span aria-hidden className="invisible block whitespace-pre font-semibold">
              {name.props.value || 'Bezeichnung'}
            </span>
            <input
              className="absolute inset-0 w-full min-w-0 bg-transparent font-semibold text-foreground outline-none placeholder:font-normal placeholder:text-muted-foreground"
              aria-label="Bezeichnung"
              title="Bezeichnung ändern — gespeichert wird beim Verlassen des Felds"
              placeholder="Bezeichnung"
              autoComplete="off"
              {...name.props}
            />
          </span>
        </h2>
        {/* Luft zwischen Text und Pfeil — hier steht auch das Zeichen fürs Speichern. */}
        <span className="flex w-8 shrink-0 items-center justify-center">
          <FeldStatusZeichen status={name} />
        </span>
        <button
          type="button"
          className="flex w-9 shrink-0 items-center justify-center rounded-r-[calc(var(--radius)-3px)] border-l border-border text-muted-foreground hover:bg-primary/15 hover:text-primary aria-expanded:bg-primary/15 aria-expanded:text-primary"
          aria-expanded={wahl.offen}
          aria-label={`${terms.positionPlural} zeigen`}
          title={titel}
          onClick={() => wahl.setOffen(!wahl.offen)}
        >
          <ChevronDown className="h-[18px] w-[18px]" />
        </button>
      </div>
      {wahl.offen && wahl.fenster}
      <FeldStatus status={name} zeichen={false} />
    </div>
  )
}

/**
 * AP-35 Detailblatt 2 8.2/8.3 — **Positionskopf und Reiter.**
 *
 * Die Kennzeile („1 PA Treppe mit Podest") ist **ein** Ausdruck aus Menge,
 * Einheit und Bezeichnung — so wird eine Position im Betrieb genannt
 * (Auftraggeber 16.9.2026), nicht als drei Spalten.
 *
 * Nachtrag 7 (Auftraggeber 19.9.2026): Status und Leistungsstand stehen rechts
 * neben der Bezeichnung wie beim Projekt, die Zeile darunter entfällt und der
 * Kopf wird um ihre Höhe niedriger. Rechts steht der Navigationsrahmen mit den
 * Aktionen der Position. Den Status wechselt ein Klick auf das Statusfeld;
 * „Freigabe widerrufen" und die Arbeitskarte sind aus dem Kopf entfallen —
 * zurück in die Vorbereitung ist der Widerruf.
 */
export function PositionDetailPanel({
  projekt,
  position,
  onChanged,
  onDeleted,
  navigation,
  zeilenStand = 0,
}: {
  /** Steigt, wenn der Papierkorb des Projekts eine Zeile zurückgeholt hat — Stückliste und Verbrauch laden neu (Nachtrag 8a). */
  zeilenStand?: number
  projekt: ProjectDetail
  position: PositionDetail
  onChanged: () => Promise<void> | void
  onDeleted: (geloescht: PositionDeleted) => Promise<void> | void
  navigation: {
    /** Nachtrag 15: Blättern ‹ › und die Aufklappliste der Positionen an der Überschrift. */
    wahl: PositionsNavigation
    alle: readonly PositionRow[]
    zuletzt: readonly string[]
    onOeffne: (id: string) => void
    onNeu?: () => void
  }
}) {
  const terms = useTerminology()
  const { user, hasModule } = useAuthStore()
  const darf = (recht: string) => erfuelltRecht(recht, user?.permissions)
  const darfPflegen = darf('projects.position.manage')
  const darfFreigeben = darf('projects.approve.execution')

  const [reiter, setReiter] = useState<ReiterKey>('details')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const abweichend = Object.values(position.inherited ?? {}).filter((v) => v && !v.inherited).length

  async function fuehreAus(aktion: () => Promise<unknown>) {
    setLaeuft(true)
    setFehler(null)
    try {
      await aktion()
      await onChanged()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * Ein Statuswechsel aus dem Statusfeld. Aus der Vorbereitung nach „Freigegeben"
   * ist die Ausführungsfreigabe (eigener Endpunkt, eigenes Recht); alles andere
   * ist ein Statuswechsel, der Server prüft den Weg.
   */
  async function statusSetzen(ziel: ProjectPositionStatus) {
    if (ziel === 'RELEASED' && position.status === 'PREPARATION') {
      await fuehreAus(() => releasePosition(projekt.id, position.id))
      return
    }
    const grundNoetig = positionStatusReasonRequired(position.status, ziel)
    const grund = grundNoetig ? await frage(`Begründung für „${POSITION_STATUS_LABELS[ziel]?.label ?? ziel}“`) : null
    if (grundNoetig && !grund?.trim()) return
    await fuehreAus(() => changePositionStatus(projekt.id, position.id, { to: ziel, note: grund?.trim() }))
  }

  async function loeschen() {
    const frageText = `${terms.position} ${position.number} löschen? Sie bleibt 30 Tage im Papierkorb und lässt sich wiederherstellen.`
    if (!(await bestaetige(frageText, { ok: 'Löschen', gefaehrlich: true }))) return
    setLaeuft(true)
    setFehler(null)
    try {
      const geloescht = await deletePosition(projekt.id, position.id)
      await onDeleted(geloescht)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
    }
  }

  const ziele = position.allowedTransitions.filter((z) =>
    z === 'RELEASED' && position.status === 'PREPARATION' ? darfFreigeben : darfPflegen,
  )
  const nachtragOffen = darfFreigeben && position.type === 'SUPPLEMENT' && !position.supplementApproval
  const freigebbar = darfFreigeben && position.status === 'PREPARATION'

  return (
    <div className="space-y-3">
      {/* Nachtrag 7: eine Zeile weniger — Status und Leistungsstand neben der Bezeichnung.
          Nachtrag 15: links davor ‹ › mit Zähler (N15-2); die Bezeichnung ist größer (N15-7) und
          klappt die Positionsliste auf (N15-3) — der Rahmen rechts trägt nur noch die Aktionen (N15-6). */}
      <div className={`${formCard} !px-4 !py-1`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-end gap-3">
          <div className="mb-1">
            <Blaettern stelle={navigation.wahl.stelle} anzahl={navigation.wahl.anzahl} onSchritt={navigation.wahl.schritt} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs leading-none text-muted-foreground">
              <span className="font-mono">Pos. {position.number}</span>
              {position.group && <span> · {position.group.number}. {position.group.name}</span>}
              {abweichend > 0 && <span> · {abweichend} Felder weichen vom {terms.project} ab</span>}
            </p>
            {/* Einzeilig: Bei 1440 px Fenster bleiben links vom Rahmen gut 400 px — ein Umbruch
                höbe den Kopf wieder um die Zeile, die hier wegfallen soll. Lange Bezeichnungen
                kürzt die Ellipse, der Tooltip trägt sie ganz. */}
            <div className="mt-1 flex min-w-0 items-center gap-2">
              <KopfBezeichnung
                projekt={projekt}
                position={position}
                gesperrt={!darfPflegen || PROJECT_POSITION_STATUSES_READONLY.includes(position.status)}
                wahl={navigation.wahl}
                onChanged={onChanged}
              />
              <StatusFeld
                status={position.status}
                anmerkung={position.statusNote}
                ziele={ziele}
                nachtragOffen={nachtragOffen}
                laeuft={laeuft}
                onZiel={(z) => void statusSetzen(z)}
                onNachtrag={() => void fuehreAus(() => approveSupplement(projekt.id, position.id, {}))}
              />
              {/* Leistungsstand wie in der Liste links: Balken und Prozent. */}
              <span
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground"
                title={`Leistungsstand ${position.progress.percent} %${
                  position.progress.source === 'MANUAL'
                    ? ' — von Hand gesetzt'
                    : position.progress.source === 'WORK_STEPS'
                      ? ' — aus den Arbeitsschritten'
                      : ''
                }`}
                aria-label={`Leistungsstand ${position.progress.percent} %`}
              >
                <span className="h-1.5 w-10 overflow-hidden rounded-full bg-muted">
                  <span className="block h-full bg-primary" style={{ width: `${Math.min(100, position.progress.percent)}%` }} />
                </span>
                {position.progress.percent} %
              </span>
              {position.type === 'SUPPLEMENT' && (
                <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                  Nachtrag N{position.supplementNo ?? ''}
                </span>
              )}
            </div>
          </div>
          </div>

          <PositionNavigator
            aktuell={position}
            alle={navigation.alle}
            zuletzt={navigation.zuletzt}
            onOeffne={navigation.onOeffne}
            onNeu={darfPflegen ? navigation.onNeu : undefined}
            onFreigeben={freigebbar ? () => void statusSetzen('RELEASED') : undefined}
            onLoeschen={darfPflegen ? () => void loeschen() : undefined}
            laeuft={laeuft}
          />
        </div>

        {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {REITER.filter(
          (r) =>
            (r.key !== 'bom' || hasModule('PROJECT_MGMT.PROCUREMENT')) &&
            ((r.key !== 'sales' && r.key !== 'consumables') || hasModule('PROJECT_MGMT.BILLING')) &&
            ((r.key !== 'actuals' && r.key !== 'controlling') ||
              (hasModule('PROJECT_MGMT.CONTROLLING') && erfuelltRecht('projects.controlling.read', user?.permissions))),
        ).map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setReiter(r.key)}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${
              reiter === r.key
                ? 'border-primary font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {r.label}
            {r.key === 'steps' && position.counts.workSteps > 0 && ` (${position.counts.workSteps})`}
            {r.key === 'files' && position.counts.files > 0 && ` (${position.counts.files})`}
          </button>
        ))}
      </div>

      {reiter === 'details' && (
        <PositionDetailsTab projekt={projekt} position={position} darfPflegen={darfPflegen} onChanged={onChanged} />
      )}

      {reiter === 'termine' && (
        <PositionTermineTab
          projekt={projekt}
          position={position}
          darfPflegen={darfPflegen}
          offen={!PROJECT_POSITION_STATUSES_READONLY.includes(position.status)}
          onChanged={onChanged}
        />
      )}

      {reiter === 'steps' && (
        <PositionWorkStepsTab projekt={projekt} position={position} onChanged={onChanged} />
      )}

      {reiter === 'bom' && <PositionBomTab key={zeilenStand} projectId={projekt.id} positionId={position.id} />}
      {reiter === 'consumables' && <PositionConsumablesTab key={zeilenStand} projectId={projekt.id} positionId={position.id} />}
      {reiter === 'actuals' && <ActualEntriesList projectId={projekt.id} positionId={position.id} />}
      {reiter === 'sales' && (
        <PositionSalesTab projectId={projekt.id} positionId={position.id} scheme={(position.effective?.billingScheme as string | undefined) ?? null} />
      )}
      {reiter === 'tech' && <PositionTechTab projectId={projekt.id} positionId={position.id} />}
      {reiter === 'controlling' && <ProjectControllingTab projectId={projekt.id} festePosition={position.id} />}

      {reiter === 'files' && (
        <ProjectFilesPanel
          projectId={projekt.id}
          entityType="POSITION"
          entityId={position.id}
          onChanged={onChanged}
        />
      )}

      {reiter === 'history' && (
        <div className={formCard}>
          <ul className="space-y-2 text-sm">
            {position.history.length === 0 && <li className="text-muted-foreground">Noch nichts geschehen.</li>}
            {position.history.map((e) => (
              <li key={e.id} className="flex gap-3 border-b border-border pb-2 last:border-0">
                <span className="w-36 shrink-0 text-xs text-muted-foreground">{alsZeitpunkt(e.approvedAt)}</span>
                <span>
                  {e.kind === 'STATUS_CHANGE'
                    ? `${positionStatusText(e.fromStatus)} → ${positionStatusText(e.toStatus)}`
                    : e.kind === 'EXECUTION'
                      ? 'Ausführungsfreigabe'
                      : e.kind === 'SUPPLEMENT_APPROVED'
                        ? 'Kundenfreigabe Nachtrag'
                        : (APPROVAL_KIND_LABELS[e.kind] ?? e.kind)}
                  {e.note && <span className="text-muted-foreground"> · {e.note}</span>}
                  {e.trigger && <span className="text-muted-foreground"> · {ausloeserText(e.trigger)}</span>}
                  {e.revokedAt && (
                    <span className="text-destructive"> · widerrufen: {e.revokeReason ?? ''}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * Nachtrag 7 — **das Statusfeld** neben der Bezeichnung: zeigt den Status wie beim
 * Projekt und öffnet per Klick die erlaubten Wechsel. Die Anmerkung zum Status
 * (z. B. der Grund einer Sperre) steht im Tooltip und oben in der Auswahl.
 */
function StatusFeld({
  status,
  anmerkung,
  ziele,
  nachtragOffen,
  laeuft,
  onZiel,
  onNachtrag,
}: {
  status: ProjectPositionStatus
  anmerkung: string | null
  ziele: readonly ProjectPositionStatus[]
  nachtragOffen: boolean
  laeuft: boolean
  onZiel: (ziel: ProjectPositionStatus) => void
  onNachtrag: () => void
}) {
  const [offen, setOffen] = useState(false)
  const feld = useRef<HTMLDivElement>(null)
  const st = POSITION_STATUS_LABELS[status]
  const bedienbar = ziele.length > 0 || nachtragOffen

  useEffect(() => {
    if (!offen) return
    const weg = (e: MouseEvent) => {
      if (feld.current && !feld.current.contains(e.target as Node)) setOffen(false)
    }
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOffen(false)
    }
    document.addEventListener('mousedown', weg)
    document.addEventListener('keydown', taste)
    return () => {
      document.removeEventListener('mousedown', weg)
      document.removeEventListener('keydown', taste)
    }
  }, [offen])

  const klasse = `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${st?.class ?? ''}`
  if (!bedienbar) {
    return (
      <span className={`${klasse} shrink-0`} title={anmerkung ?? undefined}>
        {st?.label ?? status}
      </span>
    )
  }
  return (
    <div ref={feld} className="relative shrink-0">
      <button
        type="button"
        disabled={laeuft}
        className={`${klasse} hover:opacity-80 disabled:opacity-50`}
        title={anmerkung ? `Anmerkung: ${anmerkung}` : 'Status ändern'}
        aria-haspopup="menu"
        aria-expanded={offen}
        onClick={() => setOffen((o) => !o)}
      >
        {st?.label ?? status}
        <ChevronDown className="h-3 w-3" />
      </button>
      {offen && (
        <div
          role="menu"
          className="absolute left-0 top-[calc(100%+4px)] z-50 min-w-[220px] rounded-md border border-border p-1 shadow-lg"
          style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
        >
          {anmerkung && <p className="px-2 pb-1 pt-0.5 text-xs text-muted-foreground">Anmerkung: {anmerkung}</p>}
          {ziele.length > 0 && (
            <p className="px-2 pb-1 pt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">Ändern zu</p>
          )}
          {ziele.map((z) => (
            <button
              key={z}
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
              onClick={() => {
                setOffen(false)
                onZiel(z)
              }}
            >
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${POSITION_STATUS_LABELS[z]?.class ?? ''}`}>
                {POSITION_STATUS_LABELS[z]?.label ?? z}
              </span>
            </button>
          ))}
          {nachtragOffen && (
            <button
              type="button"
              role="menuitem"
              className="mt-1 flex w-full items-center rounded border-t border-border px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
              onClick={() => {
                setOffen(false)
                onNachtrag()
              }}
            >
              Kundenfreigabe des Nachtrags vermerken
            </button>
          )}
        </div>
      )}
    </div>
  )
}
